import { ShaderProgram } from '../engine/ShaderProgram';
import { RenderDevice } from '../engine/RenderDevice';
import { Effect } from '../engine/Effect';

const DEFAULT_VERTEX_SHADER = `
    attribute vec2 av_Pos;
    uniform vec4 uv_Projection;

    #ifdef COLORING
    attribute vec4 av_Color;
    varying lowp vec4 outColor;
    #endif

    #ifdef TEXTURING
    attribute highp vec2 av_TexCoord;
    varying highp vec2 outCoord;
    #ifdef VIDEO
    varying highp vec2 outCoordAlpha;
    #endif
    #endif

    #ifdef MASK1
    uniform mediump vec2 maskTexCoord1;
    uniform mediump vec2 maskTexCoordScale1;
    uniform mat3 maskMatrix1;
    varying mediump vec2 outMaskCoord1;
    #endif

    #ifdef MASK2
    uniform mediump vec2 maskTexCoord2;
    uniform mediump vec2 maskTexCoordScale2;
    uniform mat3 maskMatrix2;
    varying mediump vec2 outMaskCoord2;
    #endif

    #ifdef TRANSFORM
    uniform mat3 um_Transform;
    #endif

    void main(void)
    {
    #ifdef TRANSFORM
        vec3 vpos = vec3(av_Pos, 1.0) * um_Transform;
    #else
        vec3 vpos = vec3(av_Pos, 1.0);
    #endif
        gl_Position = vec4(vpos.xy * uv_Projection.xy + uv_Projection.zw, 0, 1);

    #ifdef COLORING
    #ifdef PREMULTALPHA
        outColor = vec4(av_Color.rgb * av_Color.a, av_Color.a);
    #else
        outColor = av_Color;
    #endif
    #endif

    #ifdef TEXTURING
        outCoord = av_TexCoord;
    #ifdef VIDEO
        outCoordAlpha = outCoord + vec2(0.5, 0.0);
    #endif
    #endif

    #ifdef MASK1
        vec3 v1 = vpos * maskMatrix1;
        outMaskCoord1 = maskTexCoord1 + v1.xy * maskTexCoordScale1;
    #endif    

    #ifdef MASK2
        vec3 v2 = vpos * maskMatrix2;
        outMaskCoord2 = maskTexCoord2 + v2.xy * maskTexCoordScale2;
    #endif          
}`;

const DEFAULT_FRAGMENT_SHADER = `
    #ifdef TEXTURING
    varying highp vec2 outCoord;
    #ifdef VIDEO
    varying highp vec2 outCoordAlpha;
    #endif
    uniform lowp sampler2D us_0;
    #endif
    
    #ifdef COLORING
    varying lowp vec4 outColor;
    #endif
    
    #ifdef COLOR_ADJUST
    uniform lowp mat3 hue;
    uniform lowp vec3 br;
    #endif
    
    #ifdef COLOROFFSET
    uniform lowp vec3 clrOffset;
    #endif
    
    #ifdef MASK1
    uniform lowp sampler2D us_M1;
    varying mediump vec2 outMaskCoord1;
    #endif
    
    #ifdef MASK2
    uniform lowp sampler2D us_M2;
    varying mediump vec2 outMaskCoord2;
    #endif

    #ifdef FILTER
    uniform int u_filterKind;
    uniform highp float u_filterTime;
    uniform lowp float u_filterIntensity;
    uniform lowp vec4 u_filterTintColor;
    uniform highp float u_filterPixelSize;
    uniform highp vec2 u_filterCenter;
    uniform highp float u_filterRadius;
    uniform highp float u_filterStrength;
    uniform highp float u_filterAngle;
    uniform highp vec2 u_filterRedOffset;
    uniform highp vec2 u_filterGreenOffset;
    uniform highp vec2 u_filterBlueOffset;
    uniform lowp float u_filterSepia;
    #endif

    void main(void)
    {
        lowp vec4 color = vec4(1.0, 1.0, 1.0, 1.0);

    #ifdef TEXTURING
        highp vec2 sampleCoord = outCoord;
        bool earlySampled = false;
        #ifdef FILTER
            if (u_filterKind == 2) {
                sampleCoord = floor(outCoord * u_filterPixelSize) / u_filterPixelSize;
            } else if (u_filterKind == 3) {
                highp vec2 d = outCoord - u_filterCenter;
                highp float dist = length(d);
                highp float falloff = 1.0 - smoothstep(0.0, u_filterRadius, dist);
                highp float wave = sin(dist * 30.0 - u_filterTime * 5.0) * u_filterStrength * 0.05;
                sampleCoord = outCoord - normalize(d + vec2(0.0001)) * wave * falloff;
            } else if (u_filterKind == 5) {
                highp vec2 d = outCoord - u_filterCenter;
                highp float dist = length(d);
                highp float falloff = 1.0 - smoothstep(0.0, u_filterRadius, dist);
                highp float a = u_filterAngle * falloff;
                highp float c = cos(a);
                highp float s = sin(a);
                sampleCoord = u_filterCenter + mat2(c, -s, s, c) * d;
            } else if (u_filterKind == 6) {
                highp vec2 d = outCoord - u_filterCenter;
                highp float dist = length(d);
                if (dist < u_filterRadius && u_filterRadius > 0.0) {
                    highp float t = dist / u_filterRadius;
                    highp float warped = pow(t, 1.0 - u_filterStrength);
                    sampleCoord = u_filterCenter + normalize(d + vec2(0.0001)) * warped * u_filterRadius;
                }
            } else if (u_filterKind == 7) {
                color = vec4(
                    texture2D(us_0, outCoord + u_filterRedOffset).r,
                    texture2D(us_0, outCoord + u_filterGreenOffset).g,
                    texture2D(us_0, outCoord + u_filterBlueOffset).b,
                    texture2D(us_0, outCoord).a
                );
                earlySampled = true;
            } else if (u_filterKind == 10) {
                lowp vec4 cc = texture2D(us_0, outCoord);
                if (cc.a < 0.1) {
                    highp float r = u_filterRadius;
                    lowp float ax = max(
                        max(texture2D(us_0, outCoord + vec2(r, 0.0)).a,
                            texture2D(us_0, outCoord + vec2(-r, 0.0)).a),
                        max(texture2D(us_0, outCoord + vec2(0.0, r)).a,
                            texture2D(us_0, outCoord + vec2(0.0, -r)).a));
                    if (ax > 0.1) {
                        color = u_filterTintColor;
                    } else {
                        color = vec4(0.0);
                    }
                } else {
                    color = cc;
                }
                earlySampled = true;
            } else if (u_filterKind == 11) {
                lowp vec4 cc = texture2D(us_0, outCoord);
                lowp float minOut = 1.0;
                highp float r = u_filterRadius;
                for (int i = 0; i < 8; i++) {
                    highp float ang = float(i) * 0.7853982;
                    highp vec2 dir = vec2(cos(ang), sin(ang)) * r;
                    if (texture2D(us_0, outCoord + dir).a < 0.1) {
                        minOut = min(minOut, length(dir) / r);
                    }
                }
                lowp float glow = 1.0 - minOut;
                color = cc;
                color.rgb = mix(color.rgb, u_filterTintColor.rgb * cc.a, glow * u_filterIntensity * cc.a);
                earlySampled = true;
            } else if (u_filterKind == 13) {
                lowp vec4 cc = texture2D(us_0, outCoord);
                highp vec2 dir = u_filterCenter - outCoord;
                lowp vec3 acc = vec3(0.0);
                for (int i = 0; i < 16; i++) {
                    highp float t = float(i) / 16.0;
                    lowp vec4 s = texture2D(us_0, outCoord + dir * t);
                    acc += s.rgb * (1.0 - t);
                }
                color = cc;
                color.rgb += acc * u_filterIntensity * 0.05 * u_filterTintColor.rgb;
                earlySampled = true;
            } else if (u_filterKind == 14) {
                lowp vec4 cc = texture2D(us_0, outCoord);
                highp float r = u_filterRadius;
                lowp vec3 b =
                    max(vec3(0.0), texture2D(us_0, outCoord + vec2(r, 0.0)).rgb - vec3(u_filterStrength)) +
                    max(vec3(0.0), texture2D(us_0, outCoord + vec2(-r, 0.0)).rgb - vec3(u_filterStrength)) +
                    max(vec3(0.0), texture2D(us_0, outCoord + vec2(0.0, r)).rgb - vec3(u_filterStrength)) +
                    max(vec3(0.0), texture2D(us_0, outCoord + vec2(0.0, -r)).rgb - vec3(u_filterStrength));
                b *= 0.25 * u_filterIntensity;
                color = cc;
                color.rgb += b * u_filterTintColor.rgb;
                earlySampled = true;
            }
        #endif
        if (!earlySampled) {
            color = texture2D(us_0, sampleCoord);
            #ifdef VIDEO
                color.w = texture2D(us_0, sampleCoord + vec2(0.5, 0.0)).x;
            #endif
        }
    #endif
    
    #if defined COLORING
        #if defined TEXTURING
            #if defined ADD
                color = vec4(outColor.rgb + color.rgb, color.a * outColor.a);
            #elif defined MODULATE
                color = vec4(outColor * color);
            #elif defined BLEND
                #if defined PREMULTALPHA
                    color = vec4(outColor.rgb * (1.0 - color.a) + color.rgb, color.a + outColor.a);
                #else
                    color = vec4(mix(outColor.rgb, color.rgb, color.a), color.a + outColor.a);
                #endif
            #elif defined REPLACE
                color = outColor;
            #else
                color = vec4(outColor * color);
            #endif
        #else
            color = outColor;
        #endif
    #endif
    
    #ifdef COLOR_ADJUST
    #if defined PREMULTALPHA
        color = vec4(hue * color.rgb + br * color.a, color.a);
    #else
        color = vec4(hue * color.rgb + br, color.a);
    #endif
    #endif
    
    #if defined COLOROFFSET
    #if defined PREMULTALPHA
        color = color + vec4(clrOffset * color.a, 0.0);
    #else
        color = vec4(color.rgb + clrOffset, color.a);
    #endif
    #endif
    
    #ifdef MASK1
    #if defined MASKINVERT
    #if defined PREMULTALPHA
        color *= 1.0 - texture2D(us_M1, outMaskCoord1).x;
    #else
        color.a *= 1.0 - texture2D(us_M1, outMaskCoord1).x;
    #endif
    #else
    #if defined PREMULTALPHA
        color *= texture2D(us_M1, outMaskCoord1).x;
    #else
        color.a *= texture2D(us_M1, outMaskCoord1).x;
    #endif
    #endif
    #endif
    
    #ifdef MASK2
    #if defined MASKINVERT
    #if defined PREMULTALPHA
        color *= 1.0 - texture2D(us_M2, outMaskCoord2).x;
    #else
        color.a *= 1.0 - texture2D(us_M2, outMaskCoord2).x;
    #endif
    #else
    #if defined PREMULTALPHA
        color *= texture2D(us_M2, outMaskCoord2).x;
    #else
        color.a *= texture2D(us_M2, outMaskCoord2).x;
    #endif
    #endif
    #endif

    #ifdef CUSTOM1
    #ifdef PREMULTALPHA
        lowp vec3 rgb1 = color.a > 0.0 ? color.rgb / color.a : vec3(0.0);
        color = vec4((1.0 - rgb1) * color.a, color.a);
    #else
        color = vec4(1.0 - color.rgb, color.a);
    #endif
    #endif

    #ifdef FILTER
        if (u_filterKind == 1) {
            lowp float tintAmount = u_filterTintColor.a * u_filterIntensity;
            #ifdef PREMULTALPHA
                lowp vec3 unpremult = color.a > 0.0 ? color.rgb / color.a : vec3(0.0);
                lowp vec3 tinted = mix(unpremult, u_filterTintColor.rgb, tintAmount);
                color = vec4(tinted * color.a, color.a);
            #else
                color.rgb = mix(color.rgb, u_filterTintColor.rgb, tintAmount);
            #endif
        } else if (u_filterKind == 4) {
            highp float n = fract(sin(dot(outCoord + vec2(u_filterTime), vec2(12.9898, 78.233))) * 43758.5453);
            n = (n - 0.5) * 2.0 * u_filterIntensity;
            #ifdef PREMULTALPHA
                color.rgb += n * color.a;
            #else
                color.rgb += n;
            #endif
        } else if (u_filterKind == 8) {
            lowp float scanline = sin(outCoord.y * u_filterPixelSize * 3.14159) * 0.5 + 0.5;
            scanline = mix(1.0, scanline, u_filterIntensity);
            highp float vig = clamp(1.0 - length(outCoord - vec2(0.5)) * u_filterStrength, 0.0, 1.0);
            color.rgb *= scanline * vig;
        } else if (u_filterKind == 9) {
            #ifdef PREMULTALPHA
                lowp vec3 of_unp = color.a > 0.0 ? color.rgb / color.a : vec3(0.0);
            #else
                lowp vec3 of_unp = color.rgb;
            #endif
            lowp float gray = dot(of_unp, vec3(0.299, 0.587, 0.114));
            lowp vec3 sepiaColor = vec3(gray * 1.07, gray * 0.74, gray * 0.43);
            of_unp = mix(of_unp, sepiaColor, u_filterSepia);
            highp float scratch = step(0.997, fract(outCoord.x * 50.0 + u_filterTime * 0.7));
            of_unp += scratch * u_filterIntensity * 0.5;
            #ifdef PREMULTALPHA
                color = vec4(of_unp * color.a, color.a);
            #else
                color = vec4(of_unp, color.a);
            #endif
        } else if (u_filterKind == 12) {
            highp float vd = length(outCoord - vec2(0.5));
            lowp float vamt = smoothstep(u_filterRadius, u_filterRadius + u_filterStrength + 0.0001, vd) * u_filterIntensity;
            #ifdef PREMULTALPHA
                color.rgb = mix(color.rgb, u_filterTintColor.rgb * color.a, vamt);
            #else
                color.rgb = mix(color.rgb, u_filterTintColor.rgb, vamt);
            #endif
        } else if (u_filterKind == 15) {
            #ifdef PREMULTALPHA
                lowp vec3 hs_unp = color.a > 0.0 ? color.rgb / color.a : vec3(0.0);
            #else
                lowp vec3 hs_unp = color.rgb;
            #endif
            lowp float cmax = max(max(hs_unp.r, hs_unp.g), hs_unp.b);
            lowp float cmin = min(min(hs_unp.r, hs_unp.g), hs_unp.b);
            lowp float delta = cmax - cmin;
            lowp float h = 0.0;
            if (delta > 0.0001) {
                if (cmax == hs_unp.r) h = mod((hs_unp.g - hs_unp.b) / delta, 6.0);
                else if (cmax == hs_unp.g) h = (hs_unp.b - hs_unp.r) / delta + 2.0;
                else h = (hs_unp.r - hs_unp.g) / delta + 4.0;
                h *= 1.0471975;
            }
            h += u_filterAngle;
            lowp float s = cmax > 0.0001 ? delta / cmax : 0.0;
            lowp float v = cmax;
            lowp float c = v * s;
            lowp float hh = mod(h / 1.0471975, 6.0);
            lowp float xx = c * (1.0 - abs(mod(hh, 2.0) - 1.0));
            lowp vec3 rgb;
            if (hh < 1.0) rgb = vec3(c, xx, 0.0);
            else if (hh < 2.0) rgb = vec3(xx, c, 0.0);
            else if (hh < 3.0) rgb = vec3(0.0, c, xx);
            else if (hh < 4.0) rgb = vec3(0.0, xx, c);
            else if (hh < 5.0) rgb = vec3(xx, 0.0, c);
            else rgb = vec3(c, 0.0, xx);
            rgb += vec3(v - c);
            #ifdef PREMULTALPHA
                color = vec4(rgb * color.a, color.a);
            #else
                color = vec4(rgb, color.a);
            #endif
        } else if (u_filterKind == 16) {
            lowp float pl = max(2.0, u_filterPixelSize);
            #ifdef PREMULTALPHA
                lowp vec3 ps_unp = color.a > 0.0 ? color.rgb / color.a : vec3(0.0);
                ps_unp = floor(ps_unp * pl) / pl;
                color = vec4(ps_unp * color.a, color.a);
            #else
                color.rgb = floor(color.rgb * pl) / pl;
            #endif
        } else if (u_filterKind == 17) {
            highp float fd = length(outCoord - u_filterCenter);
            lowp float flHalo = 1.0 - smoothstep(0.0, max(u_filterRadius, 0.0001), fd);
            flHalo *= flHalo;
            lowp float flCore = 1.0 - smoothstep(0.0, max(u_filterRadius * u_filterStrength, 0.0001), fd);
            mediump vec3 flCoreColor = mix(u_filterTintColor.rgb, vec3(1.0), 0.85);
            mediump float flRing = 0.0;
            if (u_filterAngle > 0.0 && u_filterAngle < 1.0) {
                // кольцо (линзовый блик): узкая полоса на радиусе angle, ширина — pixelSize% от радиуса, сила — sepia
                mediump float flRingW = max(u_filterAngle * u_filterPixelSize * 0.01, 0.0005);
                flRing = 1.0 - smoothstep(0.0, flRingW, abs(fd - u_filterAngle));
                flRing *= flRing * u_filterSepia;
            }
            mediump vec3 flGlow = (u_filterTintColor.rgb * (flHalo + flRing) + flCoreColor * flCore) * u_filterTintColor.a * u_filterIntensity;
            // rgb-свет с нулевой альфой: и при PMA-блендинге (One, InvSrcAlpha), и при
            // аддитивном (One, One) даёт чистое сложение со сценой, углы квада невидимы
            color = vec4(flGlow * color.a, 0.0);
        }
    #endif

        gl_FragColor = color;
}`;

export class LazyShaderCache {
  private _renderDevice: RenderDevice;
  private _compiledPrograms: Map<number, ShaderProgram> = new Map();
  
  constructor(renderDevice: RenderDevice) {
    this._renderDevice = renderDevice;
  }

  getProgram(effectId: number): ShaderProgram | null {
    if (this._compiledPrograms.has(effectId)) {
      return this._compiledPrograms.get(effectId)!;
    }

    try {
      const defines = this._generateDefines(effectId);
      const vertexSource = this._applyDefinesToSource(DEFAULT_VERTEX_SHADER, defines);
      const fragmentSource = this._applyDefinesToSource(DEFAULT_FRAGMENT_SHADER, defines);

      const program = this._renderDevice.createShaderProgram(
        vertexSource,
        fragmentSource,
        []
      );

      if (program) {
        this._compiledPrograms.set(effectId, program);
      }

      return program;
    } catch (error) {
      console.error(`LazyShaderCache: Failed to compile shader for effectId ${effectId}:`, error);
      return null;
    }
  }

  private _generateDefines(effectId: number): string[] {
    const defines: string[] = [];

    if ((effectId & Effect.Texture0) !== 0) {
      defines.push('#define TEXTURING');
    }

    if ((effectId & Effect.Color) !== 0) {
      defines.push('#define COLORING');
      defines.push('#define MODULATE');
    }

    if ((effectId & Effect.ColorAdjust) !== 0) {
      defines.push('#define COLOR_ADJUST');
    }

    if ((effectId & Effect.ColorOffset) !== 0) {
      defines.push('#define COLOROFFSET');
    }

    if ((effectId & Effect.Mask1) !== 0) {
      defines.push('#define MASK1');
    }

    if ((effectId & Effect.Mask2) !== 0) {
      defines.push('#define MASK2');
    }

    if ((effectId & Effect.PremultAlpha) !== 0) {
      defines.push('#define PREMULTALPHA');
    }

    if ((effectId & Effect.Video) !== 0) {
      defines.push('#define VIDEO');
    }

    if ((effectId & Effect.Transform) !== 0) {
      defines.push('#define TRANSFORM');
    }

    if ((effectId & Effect.Custom1) !== 0) {
      defines.push('#define CUSTOM1');
    }

    if ((effectId & Effect.Filter) !== 0) {
      defines.push('#define FILTER');
    }

    return defines;
  }

  private _applyDefinesToSource(source: string, defines: string[]): string {
    if (defines.length === 0) {
      return source;
    }

    const defineString = defines.join('\n') + '\n';
    return defineString + source;
  }

  clearCache(): void {
    this._compiledPrograms.forEach(program => program.dispose());
    this._compiledPrograms.clear();
  }

  dispose(): void {
    this.clearCache();
  }
} 